import { useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { useSelector } from "react-redux";
import {
  TRAINING_QUICK_LESSON_IDS,
  QUICK_START_WELCOME_EVENT,
} from "./activationPath";
import { ONBOARDING_SPORTS } from "../constants/onboardingSports";
import { givenNameFromFullName } from "../utils/clientAppUtils";
import { useActivation } from "./ActivationContext";
import s from "./activation.module.css";

export function requestQuickStartWelcome(opts = {}) {
  window.dispatchEvent(
    new CustomEvent(QUICK_START_WELCOME_EVENT, { detail: opts }),
  );
}

/** First-visit welcome: pick sport, then start the live tour. */
export default function QuickStartWelcomeModal() {
  const { welcomeOpen, acceptWelcome, skipWelcome } = useActivation();
  const [pendingTraining, setPendingTraining] = useState(null);
  const [sportId, setSportId] = useState(null);
  const [customSport, setCustomSport] = useState("");
  const club = useSelector((state) => state.auth.club);
  const tenant = useSelector((state) => state.auth.tenant);
  const profile = useSelector((state) => state.auth.profile);
  const open = welcomeOpen || Boolean(pendingTraining);

  const clubName = club?.name || tenant?.name || "ваш клуб";
  const userName = useMemo(
    () => givenNameFromFullName(profile?.full_name) || null,
    [profile?.full_name],
  );

  const directionName = useMemo(() => {
    if (sportId === "other") {
      return String(customSport || "").trim();
    }
    const preset = ONBOARDING_SPORTS.find((x) => x.id === sportId);
    return preset?.name || "";
  }, [sportId, customSport]);

  const canStart = Boolean(directionName);

  useEffect(() => {
    const onRequest = (event) => {
      setPendingTraining(event.detail || {});
    };
    window.addEventListener(QUICK_START_WELCOME_EVENT, onRequest);
    return () =>
      window.removeEventListener(QUICK_START_WELCOME_EVENT, onRequest);
  }, []);

  useEffect(() => {
    if (!open) {
      setSportId(null);
      setCustomSport("");
    }
  }, [open]);

  if (!open) return null;

  const handleStart = () => {
    if (!canStart) return;
    const opts = pendingTraining;
    const fromTraining = Boolean(pendingTraining);
    setPendingTraining(null);
    acceptWelcome({
      startTraining: fromTraining,
      trainingOpts: opts || {},
      directionName,
    });
  };

  const handleSkip = () => {
    setPendingTraining(null);
    skipWelcome({ fromTraining: Boolean(pendingTraining) });
  };

  return createPortal(
    <div
      className={s.actWelcomeBackdrop}
      role="presentation"
      onClick={(e) => {
        if (e.target === e.currentTarget) handleSkip();
      }}
    >
      <div
        className={s.actWelcomeModal}
        role="dialog"
        aria-modal="true"
        aria-labelledby="act-welcome-title"
      >
        <button
          type="button"
          className={s.actWelcomeClose}
          onClick={handleSkip}
          aria-label="Закрыть"
        >
          ×
        </button>

        <div className={s.actWelcomeHero}>
          <span className={s.actWelcomeBadge}>Быстрый старт</span>
          <h2 id="act-welcome-title" className={s.actWelcomeClubName}>
            {clubName}
          </h2>
          {userName ? (
            <p className={s.actWelcomeGreeting}>Привет, {userName}</p>
          ) : null}
          <p className={s.actWelcomeLead}>
            Подсказка проведёт по живым экранам. Сначала выберите вид спорта.
          </p>
        </div>

        <div className={s.actWelcomeBody}>
          <p className={s.actWelcomeSectionTitle}>Какой у вас вид спорта?</p>
          <div className={s.actWelcomeSportGrid} role="listbox" aria-label="Вид спорта">
            {ONBOARDING_SPORTS.map((sport) => {
              const selected = sportId === sport.id;
              return (
                <button
                  key={sport.id}
                  type="button"
                  role="option"
                  aria-selected={selected}
                  className={[
                    s.actWelcomeSportChip,
                    selected ? s.actWelcomeSportChip_on : "",
                  ]
                    .filter(Boolean)
                    .join(" ")}
                  onClick={() => setSportId(sport.id)}
                >
                  {sport.name}
                </button>
              );
            })}
            <button
              type="button"
              role="option"
              aria-selected={sportId === "other"}
              className={[
                s.actWelcomeSportChip,
                sportId === "other" ? s.actWelcomeSportChip_on : "",
              ]
                .filter(Boolean)
                .join(" ")}
              onClick={() => setSportId("other")}
            >
              Другой
            </button>
          </div>

          {sportId === "other" ? (
            <input
              className={s.actWelcomeSportInput}
              value={customSport}
              onChange={(e) => setCustomSport(e.target.value)}
              placeholder="Например, плавание"
              autoFocus
              aria-label="Название вида спорта"
            />
          ) : null}

          <p className={s.actWelcomeEta}>
            {TRAINING_QUICK_LESSON_IDS.length} шагов · около 5 минут
          </p>

          <div className={s.actWelcomeActions}>
            <button
              type="button"
              className={s.actWelcomePrimary}
              onClick={handleStart}
              disabled={!canStart}
            >
              Продолжить
            </button>
            <button
              type="button"
              className={s.actWelcomeSecondary}
              onClick={handleSkip}
            >
              Пройти позже
            </button>
          </div>
        </div>
      </div>
    </div>,
    document.body,
  );
}
